/**
 * @file order-create-rows.ts
 * @description Строки состава в форме создания заказа (вынесено из
 * `OrderCreatePage` в phase19.24 шаг 5.1).
 *
 * Чистые функции без React: добавление SKU, слияние дублей, итог и проверка
 * количеств. Страница стала раскладкой (способ получения, оплата, итог), и
 * держать в ней же арифметику состава значило бы снова получить файл на 500
 * строк.
 *
 * Количество проверяем по единице СЧЁТА строки (`skuCountUnit`): «1.5 шт»
 * сервер примет, а на складе появится полпачки, которую никто не соберёт.
 */

import type { OrderSkuDto } from "./api-orders";
import { skuCountUnit, validateQty } from "./qty-input";

/** Строка формы: пустая, пока не выбран SKU. */
export interface OrderCreateRow {
  skuId: string;
  skuCode: string;
  productName: string;
  quantity: string;
  priceGross: string;
  /** «шт» для фасовки, «г» для развеса — от неё шаг поля и проверка «целое». */
  countUnit: string;
}

/** Пустая строка под inline-подбор SKU. */
export function emptyRow(): OrderCreateRow {
  return {
    skuId: "",
    skuCode: "",
    productName: "",
    quantity: "1",
    priceGross: "",
    countUnit: "шт",
  };
}

/** Строка из выбранного SKU каталога. */
export function rowFromSku(sku: OrderSkuDto, quantity = "1"): OrderCreateRow {
  return {
    skuId: sku.id,
    skuCode: sku.code,
    productName: sku.productName,
    quantity,
    priceGross: sku.priceGross,
    countUnit: skuCountUnit(sku),
  };
}

/**
 * SKU выбран в пустой строке: если такой товар уже есть выше — увеличиваем
 * его количество и убираем пустую строку (двух строк одного SKU в заказе не
 * бывает, склад соберёт их как одну).
 */
export function pickSkuInRow(
  rows: OrderCreateRow[],
  index: number,
  sku: OrderSkuDto,
): OrderCreateRow[] {
  const existingIdx = rows.findIndex((r, i) => i !== index && r.skuId === sku.id);
  if (existingIdx >= 0) {
    return rows
      .map((r, i) =>
        i === existingIdx ? { ...r, quantity: String(Number(r.quantity) + 1) } : r,
      )
      .filter((_, i) => i !== index);
  }
  return rows.map((r, i) => (i === index ? rowFromSku(sku) : r));
}

/** Добавление пачки SKU из каталога: дубли увеличивают количество. */
export function addManyRows(
  rows: OrderCreateRow[],
  skus: OrderSkuDto[],
): OrderCreateRow[] {
  const next = [...rows];
  for (const sku of skus) {
    const idx = next.findIndex((r) => r.skuId === sku.id);
    const existing = idx >= 0 ? next[idx] : undefined;
    if (existing) {
      next[idx] = { ...existing, quantity: String(Number(existing.quantity) + 1) };
    } else {
      next.push(rowFromSku(sku));
    }
  }
  return next;
}

/** Сумма товаров в рублях (без доставки). */
export function rowsTotal(rows: OrderCreateRow[]): number {
  return rows.reduce(
    (sum, r) => sum + (Number(r.priceGross) || 0) * (Number(r.quantity) || 0),
    0,
  );
}

/** Строки с выбранным товаром — только они уходят на сервер. */
export function filledRows(rows: OrderCreateRow[]): OrderCreateRow[] {
  return rows.filter((r) => r.skuId);
}

/** Первая проблема количеств человеческим текстом; `null` — всё в порядке. */
export function validateRows(rows: OrderCreateRow[]): string | null {
  for (const row of filledRows(rows)) {
    const problem = validateQty(row.quantity, row.countUnit, { allowZero: false });
    if (problem) return `«${row.productName}»: ${problem}`;
  }
  return null;
}
